import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, adminLogin } from '../api/client';

/**
 * Đăng nhập quản trị.
 *
 * Một thông báo lỗi duy nhất cho mọi lý do thất bại: server cố tình trả cùng
 * mã cho "sai email" và "sai mật khẩu", nên UI diễn giải khác nhau sẽ làm hỏng
 * công sức chống dò email ở backend.
 */
export function LoginPage({ onSignedIn }: { onSignedIn: () => void }): ReactNode {
  const { t } = useTranslation(['admin', 'errors', 'common']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setErrorCode(undefined);
    try {
      await adminLogin(email.trim(), password);
      onSignedIn();
    } catch (error) {
      setErrorCode(error instanceof ApiError ? error.code : 'api.error.internal');
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="login">
      <form className="login__card" onSubmit={(event) => void submit(event)}>
        <h1>{t('login.heading')}</h1>

        <label className="field">
          <span>{t('login.email')}</span>
          <input
            type="email"
            value={email}
            autoComplete="username"
            required
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label className="field">
          <span>{t('login.password')}</span>
          <input
            type="password"
            value={password}
            autoComplete="current-password"
            required
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {errorCode ? (
          <p className="feedback feedback--error" role="alert">
            {t(errorCode, { ns: 'errors', defaultValue: t('state.error', { ns: 'common' }) })}
          </p>
        ) : null}

        <button type="submit" disabled={pending}>
          {pending ? t('login.working') : t('login.submit')}
        </button>
      </form>
    </main>
  );
}
